import { useEffect } from "react";
import { useSearchParams } from "react-router";

/**
 * Where a table landed, shown: the paper scrolls to the block and it carries a
 * short accent outline (`data-landed`, styled in index.css), the same
 * highlight the Duplicate card uses (Olivia, tables-modern 1f: 1.2 s).
 */
export const LANDED_MS = 1200;

/** Scroll the block into view and mark it for `LANDED_MS`. Retries briefly
 *  while the canvas renders it. Returns whether the block was found. */
export function showLandedBlock(blockId: string, tries = 20): Promise<boolean> {
  return new Promise((resolve) => {
    const attempt = (left: number) => {
      const root = document.querySelector('[data-testid="flow-editor"]') ?? document;
      const el = root.querySelector<HTMLElement>(`[data-block-id="${blockId}"]`);
      if (!el) {
        if (left <= 0) { resolve(false); return; }
        window.setTimeout(() => attempt(left - 1), 100);
        return;
      }
      el.scrollIntoView?.({ behavior: "smooth", block: "center" });
      el.setAttribute("data-landed", "true");
      window.setTimeout(() => el.removeAttribute("data-landed"), LANDED_MS);
      resolve(true);
    };
    attempt(tries);
  });
}

/**
 * `?block=<id>` on a paper's URL: "Insert and open" and "Go to Table 3" land
 * here, and the paper shows the table once the canvas has it. The parameter
 * is dropped afterwards, so a reload does not flash it again.
 */
export function useLandOnBlock(): void {
  const [params, setParams] = useSearchParams();
  const block = params.get("block");
  useEffect(() => {
    if (!block) return;
    let live = true;
    void showLandedBlock(block, 80).then(() => {
      if (!live) return;
      setParams((p) => { const next = new URLSearchParams(p); next.delete("block"); return next; }, { replace: true });
    });
    return () => { live = false; };
  }, [block, setParams]);
}
